import { useContext, useEffect, useState } from "react";
import promiseConcurrency from "promise-concurrency";
import UserWalletContext from "../contexts/walletContext";
import { config } from "../config";
import { frog_look_to_left_webp, frogs, logo } from "../assets";
import { useEffectWithAbort, useLatestCallback } from "../helpers/util";
import "./Home.css";
import { PepeContractHandler } from "../pepeContractHandler";
import { TokenContractHandler } from "../tokenContractHandler";
import BtnMetamask from "../components/BtnMetamask";
import AlertContext from "../contexts/AlertContext";
import { sleep } from "../helpers/util-no-import";
import { useSearchParams } from "react-router-dom";
import { FadeSlideshow } from "../components/FadeSlideshow";
import MintedDialog from "../dialogs/MintedDialog";
import axios from "axios";

/**
 * convert 420690000 to 420,690,000
 * @param n number to format
 */
function formatNumber(n: number) {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function Home() {
  const { userWallet, clickHandler: userWalletClickHandler } =
    useContext(UserWalletContext);
  const [quantity, setQuantity] = useState<number>(1);
  const [isMinted, setIsMinted] = useState<boolean>(false);
  const [txStatus, setTxStatus] = useState<number>(0);
  const [txHash, setTxHash] = useState<string>(null);
  const { dialog } = useContext(AlertContext);
  const [mintPrice, setMintPrice] = useState<number>(config.mintPrice);
  const [totalMinted, setTotalMinted] = useState<number | void>(
    config.totalMinted
  );
  const [totalSupply, setTotalSupply] = useState<number>(config.totalSupply);
  const maxQuantity = totalSupply - (totalMinted || 0);
  const minQuantity = 1;
  if (process.env.NODE_ENV === "development")
    Object.assign(window, {
      userWallet,
      promiseConcurrency,
      formatNumber,
      dialog,
    });
  const [searchParams] = useSearchParams();
  const isDebug = searchParams.get("debug") === "1";
  useEffect(() => {
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: "https://api.frogsoverdogs.wtf/api/mint-count",
      headers: {},
    };

    axios
      .request(config)
      .then((response) => {
        if (response && response.data && response.data.mintCount);
        setTotalMinted(response.data.mintCount);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);
  useEffectWithAbort(
    async (checkAbort) => {
      if (!userWallet.web3) return;
      while (true) {
        try {
          const tokenContractHandler = new TokenContractHandler();
          await tokenContractHandler.buildContract(userWallet.web3);
          checkAbort();

          const totalMinted = await tokenContractHandler.getTotalMinted();
          checkAbort();
          setTotalMinted(+totalMinted);

          const totalSupply = await tokenContractHandler.getTotalSupply();
          checkAbort();
          setTotalSupply(+totalSupply);

          const mintPrice = await tokenContractHandler.getMintPrice();
          checkAbort();
          setMintPrice(+mintPrice / Math.pow(10, 18));
        } catch (err) {
          checkAbort();
          console.warn(err);
        }

        await sleep(1000 * 30);
        checkAbort();
      }
    },
    [userWallet.web3]
  );
  const onSubmitForm = useLatestCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!userWallet.web3 || !userWallet.account) {
        if (1) {
          userWalletClickHandler();
          return;
        }
        return alert("Please connect wallet first");
      }
      if (!quantity || quantity < minQuantity) {
        return alert("Invalid quantity");
      }
      if (typeof totalMinted !== "number") {
        return alert("Please wait for loading");
      }
      if (quantity > maxQuantity) {
        return alert("Insufficient supply");
      }
      const tokenContractHandler = new TokenContractHandler();
      await tokenContractHandler.buildContract(userWallet.web3);
      Object.assign(window, { tokenContractHandler });
      console.log("tokenContractHandler", tokenContractHandler);
      console.warn("mint", quantity);
      setTxStatus(1);
      await tokenContractHandler
        .getApproval({
          account: userWallet.account,
          quantity,
          receiptCallback: (receipt) => {
            setTxStatus(0);
            console.log("receipt", receipt);
            window.open(
              `https://${config.openseaDomain}/${userWallet.account}?search[collections][0]=${config.openseaCollectionName}`,
              "_blank"
            );
            setIsMinted(true);
          },
          approvedCallback: () => {
            setTxStatus(3);
          },
          approvaltransactionCallback: () => {
            setTxStatus(2);
          },
          transactionCallback: (hash) => {
            setTxStatus(4);
            setTxHash(hash);
          },
        })
        .catch((err) => {
          setTxHash(null);
          setTxStatus(0);
          dialog.alert(err?.message);
        });
    }
  );
  useEffectWithAbort(
    async (checkAbort) => {
      // debug
      if (process.env.NODE_ENV !== "development" && !isDebug) return;
      if (!userWallet.web3) return;
      const pepeContractHandler = new PepeContractHandler();
      await pepeContractHandler.buildContract(userWallet.web3);
      checkAbort();
      Object.assign(window, { pepeContractHandler });
      console.log("pepeContractHandler", pepeContractHandler);
      const tokenContractHandler = new TokenContractHandler();
      await tokenContractHandler.buildContract(userWallet.web3);
      checkAbort();
      Object.assign(window, { tokenContractHandler });
      console.log("tokenContractHandler", tokenContractHandler);
    },
    [userWallet.web3, isDebug]
  );
  return (
    <div className="home-page">
      <div className="container hero-section">
        <FadeSlideshow
          backgroundImage={frogs[0]}
          images={frogs.slice(1)}
          imgAttrs={{
            alt: "FROGS OVER DOGS logo",
            width: 900,
            height: 900,
          }}
        />
        <div>
          <h1>
            <img
              src={logo}
              alt="FROGS OVER DOGS logo"
              width={1107}
              height={854}
            />
          </h1>
          <div className="price-info-section">
            <div>
              {typeof mintPrice === "number" ? formatNumber(mintPrice) : "?"}{" "}
              <a href={config.pepeUniswapLink} target="_blank" rel="noreferrer">
                {config.mintCurrency}
              </a>{" "}
              each
            </div>
            <div>
              {typeof totalMinted === "number"
                ? formatNumber(totalMinted)
                : "?"}{" "}
              / {formatNumber(totalSupply)}
            </div>
          </div>
          {maxQuantity <= 0 ? (
            <div>
              <h2>Sold out</h2>
            </div>
          ) : (
            <form onSubmit={onSubmitForm}>
              <button
                type="button"
                className="quantity-btn"
                onClick={(e) => {
                  e.preventDefault();
                  setQuantity((quantity) =>
                    Math.max(minQuantity, quantity - 1)
                  );
                }}
                disabled={
                  quantity <= minQuantity ||
                  txStatus !== 0 ||
                  typeof totalMinted !== "number"
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="48"
                  viewBox="0 96 960 960"
                  width="48"
                >
                  <path d="M560 776 360 576l200-200v400Z" />
                </svg>
              </button>
              <input
                className="quantity-input"
                type="number"
                value={quantity}
                min={minQuantity}
                max={maxQuantity}
                placeholder="Quantity"
                readOnly
                onChange={(e) => {
                  setQuantity(+e.target.value);
                }}
              />
              <button
                type="button"
                className="quantity-btn"
                onClick={(e) => {
                  e.preventDefault();
                  setQuantity((quantity) =>
                    Math.min(maxQuantity, quantity + 1)
                  );
                }}
                disabled={
                  quantity >= maxQuantity ||
                  txStatus !== 0 ||
                  typeof totalMinted !== "number"
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="48"
                  viewBox="0 96 960 960"
                  width="48"
                >
                  <path d="M400 776V376l200 200-200 200Z" />
                </svg>
              </button>
              <button
                type="submit"
                disabled={txStatus !== 0 || typeof totalMinted !== "number"}
                className="mint-btn"
              >
                Mint
              </button>
            </form>
          )}
          <div className="note-text">
            {txStatus === 1 &&
              `Approve in $PEPE to pay ${formatNumber(
                mintPrice * quantity
              )} $PEPE to frogs. Note: $PEPE appears in decimal format. For “Set a spending cap for $PEPE”, you will want to either click “Use default” or enter a number for this transaction of: ${(
                (mintPrice * quantity) /
                Math.pow(10, 18)
              )
                .toFixed(18)
                .toString()
                .replace(/(\.0+|0+)$/, "")}`}
            {txStatus === 2 && `Waiting for approval...`}
            {txStatus === 3 &&
              `After you approve spending cap of $PEPE, then you will be prompted to pay gas for the mint`}
            {txStatus === 4 && txHash && (
              <div>
                View transaction on etherscan{" "}
                <a
                  href={`https://etherscan.io/tx/${txHash}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {txHash.slice(0, 2 + 5) + "…" + txHash.slice(-5)}
                </a>
              </div>
            )}
          </div>
          <BtnMetamask />
        </div>
      </div>

      <section className="description-section section-1">
        <div className="container">
          <div>
            <h2>FROGS OVER DOGS</h2>
            <p>
              Peeps are rallying around{" "}
              <a
                href={config.frogsoverdogsTwitterHashLink}
                target="_blank"
                rel="noreferrer"
              >
                #frogsoverdogs
              </a>
              , showin’ they’re here for the froggos, not the doggos. 🐸💯 No
              more doge coin or shiba inu coin memes, it’s frog time, fam!
            </p>
            <p>
              Get ready to flex those NFTs, cuz it’s frogs all the way to the
              moon! 🚀🌕🔥
            </p>
            <p>
              FROGS OVER DOGS ARE HERE TO MAKE MEMECOINS GREAT AGAIN. LAUNCHED
              STEALTH WITH NO PRESALE, AND NO RUGS JUST HUGS, FROGS OVER DOGS IS
              A NFT FOR THE FROGGOS, FOREVER. FUELED BY PURE MEMETIC POWER, LET
              FROGS OVER DOGS SHOW YOU THE WAY.
            </p>
          </div>
          <div>
            <img
              src={frog_look_to_left_webp}
              alt="pepe logo"
              width={476}
              height={483}
            />
          </div>
        </div>
      </section>

      <section className="roadmap-section">
        <div className="container">
          <h2>roadmap</h2>
          <ol>
            <li>Phase 1: Meme</li>
            <li>Phase 2: Vibe and HODL</li>
            <li>Phase 3: Generational wealth</li>
          </ol>
          <p>Be part of a squad that’s all about that frog life.</p>
          <div className="raodmap-phases">
            <div>
              <h3>Phase 1</h3>
              <ul>
                <li>
                  Launching THE $PEPE PFP NFT collection requiring $PEPE, which
                  adds value and utility to the token. $PEPE is the only way to
                  purchase! When used as avatars on Twitter/Reddit/etc, the NFT
                  will provide prime social media real estate providing maximum
                  visibility and widespread reach of $PEPE.
                </li>
                <li>420+ Holders</li>
                <li>
                  Get{" "}
                  <a
                    href={config.frogsoverdogsTwitterHashLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    #FROGSOVERDOGS
                  </a>{" "}
                  Trending on twitter with our memetic power
                </li>
              </ul>
            </div>

            <div>
              <h3>Phase 2</h3>
              <ul>
                <li>
                  Get OpenSea to add{" "}
                  <a
                    href={config.pepeUniswapLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    $PEPE
                  </a>{" "}
                  as an accepted payment token
                </li>
                <li>
                  Upon sell-out, 69 Honorary Frogs will be designed and
                  air-dropped to those who champion #frogsoverdogs the most.
                </li>
                <li>Flip Bored Ape Kennel Club</li>
              </ul>
            </div>

            <div>
              <h3>Phase 3</h3>
              <ul>
                <li>
                  We possibly hop towards an expansion to ribbit with more frog
                  buddies, ensuring current frogs get in on the expansion wave.
                </li>
                <li>6969 Holders</li>
                <li>FLIP CRYPTOPUNKS</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section-3">
        <div className="container">
          <div>
            <p>
              FROGS OVER DOGS has no association with Matt Furie or his creation
              Pepe the Frog. This NFT is simply paying homage to a meme we all
              love and recognize.
            </p>
            <p>
              FROGS OVER DOGS is a meme NFT with no intrinsic value or
              expectation of financial return. There is no formal team or
              roadmap. The NFT is completely useless and for entertainment
              purposes only.
            </p>
          </div>
        </div>
      </section>
      <MintedDialog
        isOpen={isMinted}
        onClose={() => {
          setIsMinted(false);
        }}
        userAccount={userWallet.account}
      />
    </div>
  );
}
